<template>
  <div class="grid" :class="gridClass">
    <div
      v-for="(photo, index) in photos"
      :key="index"
      class="photo"
      :class="`photo--${getPhotoClass(index, layout)}`"
      :data-photo-index="index"
      @click="$emit('photoClick', index)"
    >
      <SkeletonItem v-if="shouldShowSkeleton" class="skeleton" border-radius="0px" />
      <img
        v-else
        :src="getTileSrc(photo, index)"
        :alt="t('photoAlt', { index: index + 1 })"
        class="image"
        :width="getPreviewImageWidth(photo, 'desktop', getPhotoClass(index, layout))"
        :height="getPreviewImageHeight(photo, 'desktop', getPhotoClass(index, layout))"
        :loading="getPreviewImageLoading(index, primaryCount)"
        :fetchpriority="getPreviewImageFetchPriority(index, primaryCount)"
        decoding="async"
        @load="onImageSettled(index)"
        @error="onImageSettled(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { SkeletonItem } from '@experience/core-skeleton-lib';
import { useI18n } from '@experience/i18n';
import { computed, ref } from 'vue';

import ru from '../i18n/ru.json';
import { getLayoutSlice } from '../lib/get-displayed-photos';
import { getPhotoClass } from '../lib/get-photo-class';
import {
  getPreviewImageFetchPriority,
  getPreviewImageHeight,
  getPreviewImageLoading,
  getPreviewImageSrc,
  getPrimaryTilesCount,
  getPreviewImageWidth,
} from '../lib/preview-image';

import type { ExperiencePhoto } from '../types/experience';
import type { GalleryLayout } from '../types/gallery-layout';

interface Props {
  photos: ExperiencePhoto[];
  layout: GalleryLayout;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  photoClick: [index: number];
  heroLoad: [];
}>();

// главные плитки (главное фото и крупная плитка рядом с ним, которая тоже может стать LCP)
// грузятся сразу и с высоким приоритетом, остальные - только после них: иначе они делят с ними канал
// и откладывают LCP (fetchpriority=low этого не гарантирует)
const primaryCount = computed(() => getPrimaryTilesCount(props.layout, props.photos.length));

const settledPrimaryTiles = ref(new Set<number>());
const arePrimaryTilesSettled = computed(() => settledPrimaryTiles.value.size >= primaryCount.value);

const onImageSettled = (index: number) => {
  if (index >= primaryCount.value || arePrimaryTilesSettled.value || settledPrimaryTiles.value.has(index)) {
    return;
  }

  settledPrimaryTiles.value.add(index);

  if (arePrimaryTilesSettled.value) {
    emit('heroLoad');
  }
};

const getTileSrc = (photo: ExperiencePhoto, index: number) => {
  if (index >= primaryCount.value && !arePrimaryTilesSettled.value) {
    return undefined;
  }

  return getPreviewImageSrc(photo, 'desktop', getPhotoClass(index, props.layout));
};

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const shouldShowSkeleton = computed(() => !props.photos.length);

// раскладки рассчитаны на 4–6 фото и оставляют пустые колонки, если снимков меньше,
// поэтому для неполного набора берём раскладку по их количеству
const gridClass = computed(() => {
  const count = props.photos.length;

  return count && count < getLayoutSlice(props.layout) ? `grid--count-${count}` : `grid--${props.layout}`;
});
</script>

<style scoped lang="scss">
.grid {
  display: grid;
  grid-template-rows: repeat(2, 1fr);
  grid-template-columns: repeat(10, 1fr);
  gap: var(--alias-space-4);
  height: 480px;

  @include tablet {
    height: 360px;
  }

  @include mobile {
    display: none;
  }
}

// раскладки для неполного набора: позиционируем по индексу, ориентация фото здесь не важна
.grid--count-1 {
  grid-template-columns: 1fr;

  .photo {
    grid-row: 1 / 3;
    grid-column: 1;
  }
}

.grid--count-2 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 6;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 6 / 11;
  }
}

.grid--count-3 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 11;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 11;
  }
}

.grid--count-4 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 5;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 5 / 8;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 5 / 8;
  }

  .photo[data-photo-index='3'] {
    grid-row: 1 / 3;
    grid-column: 8 / 11;
  }
}

.grid--count-5 {
  .photo[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--vertical-horizontal {
  .photo--vertical {
    grid-row: 1 / 3;
    grid-column: 1 / 4;
  }

  .photo--horizontal {
    grid-row: 1 / 3;
    grid-column: 4 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--horizontal-horizontal {
  .photo--horizontal[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 7;
  }

  .photo--square[data-photo-index='1'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--vertical-vertical {
  .photo--vertical[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 4;
  }

  .photo--vertical[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 4 / 7;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='4'] {
    grid-row: 2;
    grid-column: 7 / 9;
  }

  .photo--square[data-photo-index='5'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.grid--square-square {
  .photo--crop[data-photo-index='0'] {
    grid-row: 1 / 3;
    grid-column: 1 / 5;
  }

  .photo--crop[data-photo-index='1'] {
    grid-row: 1 / 3;
    grid-column: 5 / 9;
  }

  .photo--square[data-photo-index='2'] {
    grid-row: 1;
    grid-column: 9 / 11;
  }

  .photo--square[data-photo-index='3'] {
    grid-row: 2;
    grid-column: 9 / 11;
  }
}

.photo {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: var(--alias-color-skeleton-default);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.skeleton {
  width: 100%;
  height: 100%;
}
</style>
